import {isUndefined} from 'lodash';
import {useEffect, useRef} from 'react';
import {Subscription, TimeoutError} from 'rxjs';
import {distinctUntilChanged, filter, first, pairwise, skipWhile, startWith, timeout} from 'rxjs/operators';
import uuid from 'uuid-random';

import {globalWindow} from '../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {isDevelopment} from '../../../libs/shared/core/src/helpers/environment/envHelpers';
import {isMobile} from '../../../libs/shared/core/src/helpers/environment/mobileEnvHelpers';
import {isChrome, isElectron} from '../../../libs/shared/core/src/helpers/environment/platformHelpers';
import {TaskSourcesEnum} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {usePrevious} from '../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {fromAnimationFrame} from '../../../libs/shared/core/src/helpers/rx/fromAnimationFrame';
import {fromVisibilityEvent, isHidden} from '../../../libs/shared/core/src/helpers/rx/visibility';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {networkMonitor} from '../../../libs/shared/core/src/services/network/networkMonitor';
import {CellsPanelPageState} from '../../../src/store/panels/cells/cellsPanelState';
import {markStep} from '../../helpers/performanceHelpers';
import {performance} from './performanceClient';

/*
 * Constants.
 */

const shouldSampleAllEventsInDev = false;
const sampleRate = 1;
const transitionTimeoutDuration = 1 * 60 * 1000;
export const appStateTransitionLogger = logger.getLogger('app-state-transition');

/*
 * Types.
 */

export enum AppTransitionStepsEnum {
  // Push socket reconnected.
  PUSH_RECONNECT = 'push_reconnect',
  // Fetched data from network.
  NETWORK_FETCH = 'network_fetch',
  // UI rendered in response to new data.
  RENDER = 'render',
}

type AppTransitionStep = {
  startTime: number;
  duration: number;
};

type ReconnectMetadata = {
  time: number;
  is_socket_connected: boolean;
  socket_state: string | undefined;
};

interface OngoingTransition {
  transitionId: string;
  startTime: number;
  appVisibleTime: number | undefined;
  reconnectMetadata: ReconnectMetadata | undefined;
  steps: Partial<{[key in AppTransitionStepsEnum]: AppTransitionStep}>;
  cleanup: () => void;
}

/*
 * State.
 */

let ongoingAppTransition: OngoingTransition | undefined;

/*
 * Api.
 */

function startAppStateTransitionTracking() {
  if (!shouldLogAppStateTransition()) {
    return undefined;
  }

  if (ongoingAppTransition) {
    // Allow only one trace at a time.
    cancelAppStateTransitionTracking();
  }

  const startTime = performance.now();
  const appTransitionId = uuid();
  markStep(performance, `App State Transition ${appTransitionId} Started`);
  appStateTransitionLogger.debug('App state transition started', {
    start_time: startTime,
  });

  const timerId = globalWindow.setInstrumentedTimeout(() => {
    // App State transition should have started at this point.
    if (ongoingAppTransition && Object.keys(ongoingAppTransition.steps).length === 0) {
      appStateTransitionLogger.debug('Ignoring app state transition.', {
        transition_id: ongoingAppTransition.transitionId,
        app_visible_time: ongoingAppTransition.appVisibleTime,
      });
      ongoingAppTransition.cleanup();
    }
  }, transitionTimeoutDuration);

  const subscription = new Subscription();

  // Because the value of "document.hidden" is not reliable when the trace starts,
  // we look for a hidden true -> false as a return to foreground signal.
  subscription.add(
    fromVisibilityEvent(globalWindow)
      .pipe(
        startWith(isHidden(globalWindow)),
        skipWhile((isWindowHidden) => !isWindowHidden),
        filter((isWindowHidden) => !isWindowHidden),
        first(),
      )
      .subscribe(() => {
        if (ongoingAppTransition) {
          ongoingAppTransition.appVisibleTime = performance.now();
        }
      }),
  );

  const cleanup = () => {
    globalWindow.clearTimeout(timerId);
    subscription.unsubscribe();
    ongoingAppTransition = undefined;
  };

  ongoingAppTransition = {
    transitionId: appTransitionId,
    steps: {},
    startTime,
    appVisibleTime: undefined,
    reconnectMetadata: undefined,
    cleanup,
  };

  return appTransitionId;
}

export function findCurrentAppTransitionTrackingId() {
  return ongoingAppTransition?.transitionId;
}

export function markAppStateTransitionStep(
  transitionId: string,
  step: AppTransitionStepsEnum,
  startTime: number,
) {
  // Transition not started or already ended.
  if (!ongoingAppTransition) {
    return;
  }

  // Ignore if current transition is different.
  const currentTransitionId = ongoingAppTransition?.transitionId;
  if (currentTransitionId && transitionId !== currentTransitionId) {
    return;
  }

  // Do not override existing steps.
  if (ongoingAppTransition.steps[step]) {
    return;
  }

  ongoingAppTransition.steps[step] = {
    startTime,
    duration: performance.now() - startTime,
  };

  appStateTransitionLogger.debug(`Marked step ${step}`, {
    step: ongoingAppTransition.steps[step],
    transition_id: ongoingAppTransition.transitionId,
  });
}

export function addReconnectMetadata(
  transitionId: string,
  isConnected: boolean,
  socketState: string | undefined,
) {
  // Transition not started or already ended.
  if (!ongoingAppTransition) {
    return;
  }

  // Ignore if current transition is different.
  const currentTransitionId = ongoingAppTransition?.transitionId;
  if (currentTransitionId && transitionId !== currentTransitionId) {
    return;
  }

  ongoingAppTransition.reconnectMetadata = {
    time: performance.now(),
    is_socket_connected: isConnected,
    socket_state: socketState,
  };
}

function finishAppStateTransitionTracking() {
  if (!ongoingAppTransition) {
    // Finishing an transition that has already ended.
    return;
  }

  const {
    transitionId: appTransitionId,
    startTime,
    appVisibleTime,
    reconnectMetadata,
    steps,
    cleanup,
  } = ongoingAppTransition;
  const now = performance.now();
  performance.measure(`App state transition`, `App State Transition ${appTransitionId} Started`);
  performance.clearMarks(`Transition ${appTransitionId} Started`);
  cleanup();

  const duration = now - startTime;
  if (duration < 0) {
    return;
  }

  if (isUndefined(appVisibleTime) && isHidden(globalWindow)) {
    // App never returned to foreground.
    appStateTransitionLogger.debug('App not in foreground when transition tracking ended', {
      transition_id: appTransitionId,
      performed_steps: steps,
      start_time: startTime,
    });
    return;
  }

  // Compute difference from the start time and send to DataDog.
  const appActivationTime = appVisibleTime ?? startTime;
  appStateTransitionLogger.info('Measured app state transition', {
    duration,
    start_time: startTime,
    app_visible_time: appActivationTime,
    reconnect_metadata: reconnectMetadata,
    steps,
    now,
    has_custom_reconnect: true,
  });
}

/**
 * Cancels an app transition tracking. Useful if transition started but never completed. This log is not sent to the server.
 * @param {string | undefined} appTransitionId The id of the app state transition.
 */
function cancelAppStateTransitionTracking() {
  if (!ongoingAppTransition) {
    // Canceling a transition that has already ended.
    return;
  }

  const {transitionId: appTransitionId, steps, startTime, cleanup} = ongoingAppTransition;
  appStateTransitionLogger.debug('Cancelling transition', {
    transition_id: appTransitionId,
    performed_steps: steps,
    start_time: startTime,
  });

  markStep(performance, `App state transition ${appTransitionId} Canceled`);
  cleanup();
}

/*
 * Helpers.
 */

function shouldLogAppStateTransition() {
  const isSupportedPlatform = isChrome() || isElectron() || isMobile();
  if (!isSupportedPlatform) {
    return false;
  }

  if (isDevelopment() && shouldSampleAllEventsInDev) {
    return true;
  }

  return Math.random() < sampleRate;
}

/*
 * Hook.
 */

export function useAppStateTransitionTracking(page: CellsPanelPageState) {
  const {isRefetching} = page;
  const wasRefetching = usePrevious(isRefetching);
  const didFinishRefetch = wasRefetching && !isRefetching;

  useEffect(() => {
    const subscription = new Subscription();
    subscription.add(
      fromVisibilityEvent(globalWindow)
        .pipe(startWith(isHidden(globalWindow)), distinctUntilChanged(), pairwise())
        .subscribe(([wasWindowHidden, isWindowHidden]) => {
          if (wasWindowHidden && !isWindowHidden) {
            startAppStateTransitionTracking();
          }

          // Stop tracking if window is hidden during tracking.
          if (isWindowHidden && findCurrentAppTransitionTrackingId()) {
            cancelAppStateTransitionTracking();
          }
        }),
    );
    subscription.add(
      networkMonitor.isOnline$.subscribe((isOnline) => {
        if (ongoingAppTransition && !isOnline) {
          cancelAppStateTransitionTracking();
        }
      }),
    );
    return () => subscription.unsubscribe();
  }, []);

  const subscriptionRef = useRef(new Subscription());

  // Stop tracking if component is unmounted.
  useEffect(
    () => () => {
      cancelAppStateTransitionTracking();
      subscriptionRef.current.unsubscribe();
    },
    [],
  );

  useEffect(() => {
    const appTransitionId = findCurrentAppTransitionTrackingId();
    if (!appTransitionId) {
      return;
    }

    if (!didFinishRefetch) {
      return;
    }

    // Refreshed data are ready to be displayed at this point
    // but animation frames are stopped while the app is in background
    // If app is still not visible 1 min after a P2 refetch, we cancel the trace.
    const renderStartTime = performance.now();
    subscriptionRef.current.add(
      fromAnimationFrame(globalWindow, {source: TaskSourcesEnum.COMPONENT_RENDER})
        .pipe(timeout(60 * 1000))
        .subscribe(
          () => {
            markAppStateTransitionStep(appTransitionId, AppTransitionStepsEnum.RENDER, renderStartTime);
            finishAppStateTransitionTracking();
          },
          (error) => {
            if (error instanceof TimeoutError) {
              cancelAppStateTransitionTracking();
            }
          },
        ),
    );
  }, [didFinishRefetch]);
}
